/** How long a node takes to move to its new place, and the view with it (see `useAnimatedPositions`). */
export const MOVE_MS = 300;

/** The viewer's reduced-motion setting, looked up once; `null` outside a browser. */
const reducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

/** The duration of an animated view change: `ms`, or 0 when the viewer asked for reduced motion. */
export const motionDuration = (ms: number, reduced = reducedMotion?.matches ?? false) => (reduced ? 0 : ms);

/** The point of a cubic Bézier from (0, 0) to (1, 1) at parameter `s`, on one axis. */
const bezierAt = (a: number, b: number, s: number) => 3 * a * s * (1 - s) ** 2 + 3 * b * s * s * (1 - s) + s ** 3;

/** The CSS `cubic-bezier(x1, y1, x2, y2)` timing function, for x1 and x2 in [0, 1], as a function of time. */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  return (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    // x rises with s when x1 and x2 are in [0, 1]; 30 halvings find the s whose x is t to 1e-9.
    const [lo, hi] = Array.from({ length: 30 }).reduce<[number, number]>(
      ([low, high]) => {
        const mid = (low + high) / 2;
        return bezierAt(x1, x2, mid) < t ? [mid, high] : [low, mid];
      },
      [0, 1],
    );
    return bezierAt(y1, y2, (lo + hi) / 2);
  };
}

/**
 * Tailwind's `ease-out`, the timing of every node move (`useAnimatedPositions`). A view move that runs with it for
 * the same duration keeps pace with the nodes, so a node the view follows holds still on screen while the others
 * move.
 */
export const MOVE_EASE = cubicBezier(0, 0, 0.2, 1);
